import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, noop, map, switchMap, tap } from 'rxjs';

import { AccountService } from 'app/core';
import { FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { Account } from 'app/core/models';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { PanelsInterfaceComponentBase, PanelsInterfaceComponent } from '../panels-interface.component';

@Component({
  selector: 'project-panel-scenarios',
  templateUrl: './scenarios.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsScenariosComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  @Input() project: Project;
  @Input() account: Account;
  @Input() panelDef: any;
  @Input() data: any;

  projectVersions$: Observable<Project[]>;
  fields$: Observable<SelectableFieldDef[]>;
  displayColumns: string[] = [];
  fieldDefs: FieldDef[] = [];

  get options() {
    return this.data?.options;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly _projectsService: ProjectsService,
    private readonly _accountService: AccountService,
    protected readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super(changeDetectorRef);
  }

  ngOnInit() {
    this._projectsService.project$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project: Project) => {
      this.project = project;
      this.changeDetectorRef.markForCheck();
    });

    this._loadFields();

    this.displayColumns = this.panelDef.meta?.fields || [];

    this.projectVersions$ = this._projectsService
      .getVersionsByProjectId(+this.project.id)
      .pipe(takeUntilDestroyed(this.destroyRef));
  }

  private _loadFields(): void {
    this.fields$ = this._accountService.getFieldDefsByAccountId(this.account.id).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((fields) => {
        this.fieldDefs = fields;
        return fields
          .filter(({ related_to_type }) => related_to_type === 'Project')
          .map((field) => ({
            ...field,
            selected: this.displayColumns.includes(field.name),
          }));
      }),
    );
  }

  onSaveColumns(selectableFieldDefs: SelectableFieldDef[]): void {
    this.displayColumns = selectableFieldDefs.map((fieldDef) => fieldDef.name);

    let meta;
    this._projectsService
      .getProjectTemplateByRelatedToId(Number(this.project.id), RelatedToType.Project)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((projectTemplate) => {
          const { panelIndex, laneIndex, tabIndex } = this._getLaneAndPanelIndexes(
            projectTemplate.definition.dashboard,
          );

          const template: ProjectTemplate<any> = JSON.parse(JSON.stringify(projectTemplate));
          if (tabIndex > -1) {
            meta = {
              ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex].meta,
              fields: this.displayColumns,
            };

            template.definition.dashboard.tabs[tabIndex].panels[panelIndex] = {
              ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex],
              meta,
            };
          } else {
            meta = {
              ...template.definition.dashboard.panels[laneIndex][panelIndex].meta,
              fields: this.displayColumns,
            };

            template.definition.dashboard.panels[laneIndex][panelIndex] = {
              ...template.definition.dashboard.panels[laneIndex][panelIndex],
              meta,
            };
          }

          return this._projectsService.updateProjectTemplate(template);
        }),
        tap(() => this.changeDetectorRef.detectChanges()),
      )
      .subscribe(noop);
  }

  private _getLaneAndPanelIndexes(dashboard: { panels: any[]; tabs: any[] }): {
    laneIndex: number;
    panelIndex: number;
    tabIndex: number;
  } {
    const { panels, tabs } = dashboard;

    let panelIndex = -1;

    const laneIndex = panels.findIndex((panels) => {
      panelIndex = panels.findIndex((panel) => panel.id === this.panelDef.id);
      return panelIndex > -1;
    });

    let tabIndex = -1;

    if (laneIndex < 0) {
      tabIndex = tabs.findIndex((tab) => {
        panelIndex = tab.panels.findIndex((panel) => panel.id === this.panelDef.id);
        return panelIndex > -1;
      });
    }

    return { panelIndex, laneIndex, tabIndex };
  }

  public lookupField(name) {
    const fieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fieldDefs.length > 0) {
      return fieldDefs[0];
    } else {
      return null;
    }
  }

  canEditDeal(): boolean {
    return this.project?.permissions?.is_editable;
  }
}
